// StoryRail — stage-② left rail: the moments the walk visited, in story
// order, with branch turns; below them the moments that CORRECTLY never ran
// (only asserted once the run completed with a declared end/stop — an aborted
// run must not claim its unreached moments were "correct").
//
// Everything derives from the store: actualPath + topology frames + branch
// activation records + the runStory terminus. Clicking a moment selects its
// first visited node so its evidence opens in the detail panel.

import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFrames, selectScenarioToTopologyMap } from '../../store/slices/topologySlice.ts';
import {
  selectActualPath,
  selectActivatedBranchRecords,
  selectSimulationStatus,
} from '../../store/slices/simulationSlice.ts';
import { selectNode, toggleSidebar, selectSidebarOpen } from '../../store/slices/uiSlice.ts';
import { selectTerminus } from '../simulation/runStory.ts';

interface WalkedFrame {
  frameId: string;
  index: number;
  moment: string;
  /** First visited node (scenario-namespace id) — evidence target. */
  firstNodeId: string;
  route: string | null;
}

export default function StoryRail() {
  const dispatch = useDispatch();
  const frames = useSelector(selectFrames);
  const scenarioToTopo = useSelector(selectScenarioToTopologyMap);
  const actualPath = useSelector(selectActualPath);
  const branchRecords = useSelector(selectActivatedBranchRecords);
  const status = useSelector(selectSimulationStatus);
  const terminus = useSelector(selectTerminus);
  const sidebarOpen = useSelector(selectSidebarOpen);

  const nodeToFrame = useMemo(() => {
    const map = new Map<string, number>();
    frames.forEach((f, i) => f.nodes.forEach((n) => map.set(n.id, i)));
    return map;
  }, [frames]);

  const routeByFrame = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of branchRecords) map.set(r.frameId, r.route);
    return map;
  }, [branchRecords]);

  const { walked, never } = useMemo(() => {
    const walkedFrames: WalkedFrame[] = [];
    const seen = new Set<number>();
    for (const nodeId of actualPath) {
      const topoId = scenarioToTopo[nodeId] ?? nodeId;
      const idx = nodeToFrame.get(topoId);
      if (idx === undefined || seen.has(idx)) continue;
      seen.add(idx);
      const frame = frames[idx];
      walkedFrames.push({
        frameId: frame.id,
        index: idx,
        moment: frame.moment,
        firstNodeId: nodeId,
        route: routeByFrame.get(frame.id) ?? null,
      });
    }
    const neverFrames = frames
      .map((f, i) => ({ frame: f, index: i }))
      // A moment that holds no node (a note, Moment ADR-040 D3) is narration: it
      // can neither run nor fail to, so it is never "correctly never ran".
      .filter(({ index, frame }) => !seen.has(index) && frame.nodes.length > 0);
    return { walked: walkedFrames, never: neverFrames };
  }, [actualPath, scenarioToTopo, nodeToFrame, frames, routeByFrame]);

  if (walked.length === 0) return null;

  const currentFrame =
    actualPath.length > 0
      ? nodeToFrame.get(
          scenarioToTopo[actualPath[actualPath.length - 1]] ?? actualPath[actualPath.length - 1],
        )
      : undefined;

  // "Correctly never ran" is only claimable for a completed, declared ending.
  const completedDeclared =
    status === 'completed' &&
    (terminus?.reason === 'terminal-end' ||
      terminus?.reason === 'declared-stop' ||
      terminus?.reason === 'neutral-end');
  const aborted = status === 'failed';

  const openEvidence = (nodeId: string) => {
    dispatch(selectNode(nodeId));
    if (!sidebarOpen) dispatch(toggleSidebar());
  };

  return (
    <div className="fv2-story-rail" data-screen="story-rail">
      <div className="fv2-rail-head">THE STORY · {walked.length} MOMENTS WALKED</div>
      {walked.map((w) => {
        const isCur = w.index === currentFrame;
        return (
          <button
            key={w.frameId}
            className={`fv2-rail-item${isCur ? ' fv2-rail-cur' : ''}`}
            onClick={() => openEvidence(w.firstNodeId)}
            title="Open this moment's evidence"
          >
            <span className="fv2-rail-no">{String(w.index).padStart(2, '0')}</span>
            <span>
              {w.moment}
              {w.route && <span className="fv2-rail-route">⑂ took {w.route}</span>}
            </span>
          </button>
        );
      })}
      {never.length > 0 && (completedDeclared || aborted) && (
        <>
          <div className="fv2-rail-head fv2-rail-head-never">
            {aborted ? 'NOT REACHED — RUN ABORTED' : 'CORRECTLY NEVER RAN'}
          </div>
          {never.slice(0, 6).map(({ frame, index }) => (
            <div key={frame.id} className="fv2-rail-never">
              <span className="fv2-rail-no">{String(index).padStart(2, '0')}</span>
              <span>{frame.moment}</span>
            </div>
          ))}
          {never.length > 6 && (
            <div className="fv2-rail-never">
              <span className="fv2-rail-no">…</span>
              <span>+ {never.length - 6} more</span>
            </div>
          )}
          <div className="fv2-rail-why">
            {aborted
              ? 'the run broke off early — these moments prove nothing this run'
              : terminus?.reason === 'declared-stop'
                ? 'a refused precondition stops the journey cold'
                : 'the journey ends where it declares — nothing more may run'}
          </div>
        </>
      )}
    </div>
  );
}
